import { Request } from "../stores/Requests20";
import { handleServiceResponse } from "./ServerRequestsInternal";
import type { FulfillmentType } from "../model/ArticleTypes";
import { mapToQuery } from "../utils/UrlUtil";
import { CHECKOUT_LIFESPAN_MINUTES } from "../stores/CheckoutTimerStore";
import { CreditCheckInfo } from "../stores/CheckoutTypes";
import { formatUTCTime } from "../utils/DateUtil";

const BASE_URL = "/ordercapture/paymentmethods";
const TIMEOUT = 5000;
type MethodsResponse = {
	methods: PaymentMethod[];
};
export type PaymentMethod = ValidPayment | InvalidPayment;
export type PaymentId = {
	allowance: string;
	type: string | null | undefined;
};
type BasePaymentMethod = {
	type: "ValidPayment" | "InvalidPayment";
	paymentId: PaymentId;
	priority: number;
	isEmployeeOnly: boolean;
};
export type ValidPayment = BasePaymentMethod & {
	type: "ValidPayment";
	payableFulfillmentTypes: FulfillmentType[];
	dailyDowntimePeriod: DowntimeConfig | null | undefined;
	isTransactional: boolean;
};
export type InvalidPayment = BasePaymentMethod & {
	type: "InvalidPayment";
	unavailabilityReasons: UnavailabilityReason[];
};
export type UnavailabilityReason = ThresholdNotReached | CustomerPaymentBlocked | ArticlePropsDisallowed | InDowntime;
export type ThresholdNotReached = {
	type: "ThresholdNotReached";
	value: number;
};
export type CustomerPaymentBlocked = {
	type: "CustomerPaymentBlocked";
	reason: string;
};
type ArticlePropsDisallowed = {
	type: "ArticlePropsDisallowed";
	priceAdjustments: string[];
};
type InDowntime = {
	type: "InDowntime";
	dailyDowntimeConfig: DowntimeConfig;
};
type DowntimeConfig = {
	startTime: string;
	endTime: string;
};
export type Transactionality = "TRANSACTIONAL_ONLY" | "NON_TRANSACTIONAL_ONLY";
type GetPaymentMethodsParams = {
	country: string;
	customerId: string;
	cardholderNumber: string;
	fulfillmentTypes: FulfillmentType[];
	transactionality?: Transactionality | null;
	priceAdjustments?: string[];
	totalPrice?: number | null;
	isEmployee?: boolean;
	excludeTransactional?: boolean;
};
const emptyMethodsResponse = {
	methods: [],
};
export async function getPaymentMethods({
	country,
	customerId,
	cardholderNumber,
	fulfillmentTypes,
	transactionality,
	priceAdjustments,
	totalPrice,
	isEmployee,
}: GetPaymentMethodsParams): Promise<MethodsResponse> {
	// @ts-expect-error
	const queryParams = new Map([
		["customerId", customerId],
		["cardholderNumber", cardholderNumber],
		["userType", isEmployee ? "employee" : "customer"],
		["transactionality", transactionality],
		["priceAdjustments", priceAdjustments],
		["fulfillmentTypes", fulfillmentTypes],
		["totalPrice", totalPrice],
		["startTimeUtc", formatUTCTime(new Date())],
		["lookaheadMinutes", CHECKOUT_LIFESPAN_MINUTES],
	]);
	const url = `${BASE_URL}/payments/${country}/methods?${mapToQuery(queryParams)}`;
	const response = Request.get(url).timeout(TIMEOUT).execute();
	return (await handleServiceResponse(response)).data || emptyMethodsResponse;
}
type SplitsResponse = {
	mainPaymentGroups: MainPaymentGroup[];
};
export type MainPaymentGroup = {
	payableFulfillmentTypes: FulfillmentType[];
	isTransactional: boolean;
};
type GetPaymentSplitsParams = {
	country: string;
	customerId: string;
	cardholderNumber: string;
	fulfillmentTypes: FulfillmentType[];
	excludeTransactional?: boolean;
	priceAdjustments?: string[];
	totalPrice?: number | null;
	isEmployee?: boolean;
};
const emptySplitsResponse = {
	mainPaymentGroups: [],
};
export async function getPaymentSplits({
	country,
	customerId,
	cardholderNumber,
	fulfillmentTypes,
	excludeTransactional,
	totalPrice,
	isEmployee,
}: GetPaymentSplitsParams): Promise<SplitsResponse> {
	// @ts-expect-error
	const queryParams = new Map([
		["customerId", customerId],
		["cardholderNumber", cardholderNumber],
		["userType", isEmployee ? "employee" : "customer"],
		["excludeTransactional", excludeTransactional],
		["fulfillmentTypes", fulfillmentTypes],
		["totalPrice", totalPrice],
		["startTimeUtc", formatUTCTime(new Date())],
		["lookaheadMinutes", CHECKOUT_LIFESPAN_MINUTES],
	]);
	const url = `${BASE_URL}/payments/${country}/splits?${mapToQuery(queryParams)}`;
	const response = Request.get(url).timeout(TIMEOUT).execute();
	return (await handleServiceResponse(response)).data || emptySplitsResponse;
}
export async function getCreditCheck(
	country: string,
	customerId: string,
	customerNumber?: string | null,
	homeStore?: string | null,
): Promise<CreditCheckInfo> {
	let requestUrl = `${BASE_URL}/creditcheck/${country}/${customerId}`;

	if (!!customerNumber && !!homeStore) {
		requestUrl = requestUrl + `?customerNumber=${customerNumber}&homeStoreId=${homeStore}`;
	}

	const responsePromise = Request.get(requestUrl).timeout(TIMEOUT).execute();
	return (await handleServiceResponse(responsePromise)).data;
}
